import React from "react";
import Reducer from "./reducer";
import { useAuth } from "shared/context/auth";
import { useCopies } from "shared/hooks/useCopies";
import InventoryType from "shared/enums/InventoryType";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import { getAssignedBulkTicket, getBulkTicketPurchaseSummary } from "./utils";
import { getOverlappingPaymentPendingInventoryForms } from "apps/myExperience/utils";
import { useGetBulkTicketDetails, useGetBulkTicketForms, useRevokeTicket } from "./dataloader";
import { useMyExperienceDashboardDetails } from "apps/myExperience/context/dashboard";
import {
  AssignedTicket,
  BulkTicketManagementProps,
  BulkTicketManageService,
  ReducerDispatch,
  RevokeTicketHandlerFunction,
  State
} from "./types";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export default function useBulkTicketManageService(props: BulkTicketManagementProps): BulkTicketManageService {
  const auth = useAuth();
  const copies = useCopies();
  useLogAnalyticsScreenHook({ properties: { screenType: AnalyticsScreenNameEnum.MANAGE_BULK_TICKET } });
  const [state, dispatch] = useReducerWrapper<State, ReducerDispatch>(Reducer);
  const { data: dashboardData, refetch: refetchDashboard } = useMyExperienceDashboardDetails();
  const [selectedTicketFormId, setSelectedTicketFormId] = React.useState<string>("");
  const [selectedBulkTicketId, setSelectedBulkTicketId] = React.useState<string>("");
  const [isTransferModalOpen, setIsTransferModalOpen] = React.useState<boolean>(false);
  const { data: bulkTicketForms = [] } = useGetBulkTicketForms({
    registrationParticipantId: auth.userDetail?.id ?? ""
  });

  const registrationParticipantId = auth.userDetail?.id ?? "";

  const { data = [], refetch } = useGetBulkTicketDetails({
    registrationParticipantId: auth.userDetail?.id
  });

  const { mutateAsync: revokeTicket } = useRevokeTicket();
  const selectedTicketFormNameByTicketFormId = React.useMemo(() => {
    return bulkTicketForms.find(item => item.formId === selectedTicketFormId)?.formName ?? "";
  }, [bulkTicketForms, selectedTicketFormId]);

  const selectedTicketFormNameByBulkTicketId = React.useMemo(() => {
    return data.find(item => item.id === selectedBulkTicketId)?.ticketFormName ?? "";
  }, [data, selectedBulkTicketId]);

  const assignedTicketList = React.useMemo(() => {
    return data.reduce((previousValue, ticketDetail) => {
      const validAssignedBulkTickets = getAssignedBulkTicket(ticketDetail.assignedTickets);
      return [...previousValue, ...validAssignedBulkTickets];
    }, [] as AssignedTicket[]);
  }, [data]);

  const purchaseSummary = React.useMemo(() => getBulkTicketPurchaseSummary(data), [data]);

  const noRecordMessage = React.useMemo(() => {
    if (purchaseSummary.total === 0) {
      return copies.get("buy.bulk.ticket.table.message");
    }

    if (purchaseSummary.pending + purchaseSummary.registered === 0) {
      return copies.get("assign.bulk.ticket.table.message");
    }
  }, [copies, purchaseSummary.pending, purchaseSummary.registered, purchaseSummary.total]);

  const openAssignModalHandler = React.useCallback(
    (bulkTicketId: string) => {
      dispatch.setAssignTicketModal(true);
      setSelectedBulkTicketId(bulkTicketId);
    },
    [dispatch]
  );

  const openTransferModalHandler = React.useCallback(() => {
    setIsTransferModalOpen(true);
  }, []);

  const closeTransferModalHandler = React.useCallback(() => {
    setIsTransferModalOpen(false);
  }, []);

  const closeAssignModalHandler = React.useCallback(() => {
    dispatch.setAssignTicketModal(false);
    setSelectedBulkTicketId("");
  }, [dispatch]);

  const revokeTicketHandler: RevokeTicketHandlerFunction = React.useCallback(
    (ticketId, isTicketAssignmentPending, uiVersion, isLoggedInUser) => {
      dispatch.setAlertConfig({
        message: isTicketAssignmentPending
          ? copies.get("cancel.bulk.ticket.message")
          : copies.get("revoke.bulk.ticket.message"),
        handleConfirm: async () => {
          try {
            await revokeTicket({
              ticketId,
              uiVersion
            });
            refetch();
            if (isLoggedInUser && !props.isOpenInModal) {
              // After revoke ticket, get Ticket option will get enabled, Refreshing dashboard to show Get Ticket
              refetchDashboard();
            }
          } catch (err) {}
          dispatch.setAlertConfig();
        },
        handleClose: () => dispatch.setAlertConfig()
      });
    },
    [copies, dispatch, props.isOpenInModal, refetch, refetchDashboard, revokeTicket]
  );

  const openBuyMoreModalHandler = React.useCallback(
    (ticketFormId: string) => {
      setSelectedTicketFormId(ticketFormId);
      dispatch.setBuyMoreTicket(true);
    },
    [dispatch]
  );

  const closeBuyMoreModalHandler = React.useCallback(() => {
    dispatch.setBuyMoreTicket(false);
    setSelectedTicketFormId("");
  }, [dispatch]);

  const hasValidBulkTicketPurchases = React.useMemo(() => {
    return data.some(item => item.valid);
  }, [data]);

  const paymentPendingInventoryForms = React.useMemo(
    () =>
      getOverlappingPaymentPendingInventoryForms(
        InventoryType.BULK_TICKET,
        dashboardData,
        bulkTicketForms?.map(form => ({ ...form, id: form.formId })) as Array<any>
      ),
    [dashboardData, bulkTicketForms]
  );

  return {
    state,
    selectedBulkTicketId,
    data,
    refetch,
    revokeTicketHandler,
    assignedTicketList,
    openAssignModalHandler,
    closeAssignModalHandler,
    purchaseSummary,
    openBuyMoreModalHandler,
    closeBuyMoreModalHandler,
    noRecordMessage,
    bulkTicketForms,
    selectedTicketFormId,
    isTransferModalOpen,
    openTransferModalHandler,
    closeTransferModalHandler,
    registrationParticipantId,
    hasValidBulkTicketPurchases,
    selectedTicketFormNameByTicketFormId,
    selectedTicketFormNameByBulkTicketId,
    paymentPendingInventoryForms
  };
}
